import * as THREE from 'three';

export interface MapGeoPosition {
    lat: number;
    lng: number;
    elevationMeters?: number;
}

export type MapHeightRevisionListener = () => void;

/**
 * Renderer-independent map surface contract.
 *
 * Geo projection and terrain height deliberately live behind one boundary:
 * routes, pins, user location and camera collision must never disagree about
 * coordinate orientation or scene scale. A null height means that the surface
 * is outside its footprint or that the required terrain tile is not loaded.
 */
export interface IMapSurface {
    /** Scene units per true horizontal metre. */
    readonly worldUnitsPerMeter: number;

    /** Projects WGS84 into the renderer's world coordinate system. */
    geoToWorld(lat: number, lng: number, elevationMeters?: number): THREE.Vector3 | null;

    /** Inverse projection used by cameraChanged and diagnostics. */
    worldToGeo(world: THREE.Vector3): MapGeoPosition | null;

    /** Terrain-only height. Props and marker geometry must never participate. */
    getWorldY(x: number, z: number): number | null;

    /** True when X/Z belongs to the configured surface footprint. */
    containsWorld(x: number, z: number): boolean;

    /** Optional LOD notification. Implementations return an unsubscribe function. */
    onHeightsChanged?(listener: MapHeightRevisionListener): () => void;
}

export function metresToWorld(surface: IMapSurface, metres: number): number {
    return metres * surface.worldUnitsPerMeter;
}

export function worldToMetres(surface: IMapSurface, worldUnits: number): number {
    return worldUnits / surface.worldUnitsPerMeter;
}
